import React, { useState } from "react";
import { toast } from "sonner";
import { createPost, uploadSingleFile } from "../../services/api";
import { FileMetadataDto, PostRequestDto } from "../../types/types";
import { PhotoIcon, VideoCameraIcon, XMarkIcon } from "@heroicons/react/24/solid";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import { VIDEO_UPLOAD_ENABLED } from "../../config/featureFlags";

export const CreatePostSection: React.FC = () => {
  const loginResponse = JSON.parse(localStorage.getItem("loginResponse") || "{}");
  const username = loginResponse.username;
  const firstName = loginResponse.firstName;
  const lastName = loginResponse.lastName;
  const profileImage = loginResponse.profilePicture;

  const [showModal, setShowModal] = useState(false);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [uploadedFile, setUploadedFile] = useState<FileMetadataDto | null>(null);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [titleError, setTitleError] = useState("");

  const { t } = useTranslation("dashboard");

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files || e.target.files.length === 0) return;
    const file = e.target.files[0]; // Only single file
    setSelectedFile(file);
    setPreviewUrl(URL.createObjectURL(file));
  };

  const handleRemoveFile = () => {
    setSelectedFile(null);
    setPreviewUrl(null);
    setUploadedFile(null);
  };

  const handleSubmit = async () => {
    const trimmedTitle = title.trim();
    const trimmedDescription = description.trim();

    if (!trimmedTitle) {
      setTitleError(t("title_required"));
      return;
    }

    try {
      setTitleError("");
      setLoading(true);
      setUploading(true);

      let uploadedData: FileMetadataDto | null = null;

      if (selectedFile) {
        uploadedData = await uploadSingleFile(selectedFile);
        setUploadedFile(uploadedData);
      }

      const dto: PostRequestDto = {
        title: trimmedTitle,
        description: trimmedDescription,
        fileMetadataDto: uploadedData || undefined,
      };

      await createPost(username, dto);
      toast.success(t("success"));

      setTitle("");
      setDescription("");
      setPreviewUrl(null);
      setSelectedFile(null);
      setUploadedFile(null);
      setTitleError("");
      setShowModal(false);
    } catch (error: any) {
      const errMsg =
        error?.response?.data?.errors?.[0] ||
        error?.response?.data?.message ||
        error.message ||
        t("error");
      toast.error(errMsg);
    } finally {
      setLoading(false);
      setUploading(false);
    }
  };

  const canPost = title.trim() || description.trim() || selectedFile;

  return (
    <div className="col-span-12 md:col-span-6 p-3">
      {!showModal && (
        <div
          onClick={() => setShowModal(true)}
          className="flex items-center gap-3 border border-gray-200 rounded-full px-3 py-2 cursor-pointer hover:bg-gray-50 transition"
        >
          {profileImage ? (
            <img
              src={profileImage}
              alt="Profile"
              className="w-10 h-10 rounded-full object-cover"
            />
          ) : (
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white font-semibold shadow-sm">
              {`${firstName?.[0] || ""}${lastName?.[0] || ""}`}
            </div>
          )}
          <div className="flex-1 text-gray-500 text-sm text-left">{t("startPost")}</div>
        </div>
      )}

      <AnimatePresence>
        {showModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50"
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="bg-white rounded-2xl shadow-2xl w-full max-w-lg p-5 relative"
            >
              <button
                onClick={() => setShowModal(false)}
                className="absolute top-3 right-3 text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="w-5 h-5" />
              </button>

              <div className="flex items-center gap-3 mb-4">
                {profileImage ? (
                  <img
                    src={profileImage}
                    alt="Profile"
                    className="w-10 h-10 rounded-full object-cover"
                  />
                ) : (
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white font-semibold shadow-sm">
                    {`${firstName?.[0] || ""}${lastName?.[0] || ""}`}
                  </div>
                )}
                <div>
                  <h2 className="font-semibold text-sm text-gray-800">{firstName} {lastName}</h2>
                </div>
              </div>

              <input
                type="text"
                placeholder={t("title")}
                value={title}
                onChange={(e) => {
                  setTitle(e.target.value);
                  if (e.target.value.trim()) setTitleError("");
                }}
                className={`mb-2 w-full border rounded-md px-3 py-2 text-sm outline-none focus:ring-1 placeholder-gray-400 ${
                  titleError
                    ? "border-red-300 focus:ring-red-500"
                    : "border-gray-200 focus:ring-blue-500"
                }`}
              />
              {titleError && (
                <p className="-mt-1 mb-2 text-xs text-red-500">
                  {titleError}
                </p>
              )}

              <textarea
                placeholder={t("description")}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                className="w-full resize-none outline-none text-sm placeholder-gray-400 mb-3 h-28 border border-gray-100 rounded-md p-2 focus:ring-1 focus:ring-blue-500"
              />

              {previewUrl && (
                <div className="flex gap-2 overflow-x-auto mb-3">
                  <div className="relative w-24 h-24 rounded-lg overflow-hidden border border-gray-300 flex-shrink-0">
                    {selectedFile?.type.startsWith("image/") ? (
                      <img src={previewUrl} alt="Preview" className="w-full h-full object-cover" />
                    ) : (
                      <video src={previewUrl} className="w-full h-full object-cover" controls />
                    )}
                    <button
                      onClick={handleRemoveFile}
                      className="absolute top-1 right-1 bg-black bg-opacity-60 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs"
                      type="button"
                    >
                      ×
                    </button>
                  </div>
                </div>
              )}

              <div className="flex items-center justify-between border-t pt-3 mt-2">
                <div className="flex gap-4">
                  <label className="flex items-center gap-1 text-blue-500 text-sm cursor-pointer hover:text-blue-600">
                    <PhotoIcon className="w-5 h-5" />
                    <span>{t("photo")}</span>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleFileChange}
                      className="hidden"
                      disabled={uploading}
                    />
                  </label>

                  {VIDEO_UPLOAD_ENABLED && (
                    <label className="flex items-center gap-1 text-blue-500 text-sm cursor-pointer hover:text-blue-600">
                      <VideoCameraIcon className="w-5 h-5" />
                      <span>{t("video")}</span>
                      <input
                        type="file"
                        accept="video/*"
                        onChange={handleFileChange}
                        className="hidden"
                        disabled={uploading}
                      />
                    </label>
                  )}
                </div>


                {canPost && (
                  <button
                    onClick={handleSubmit}
                    disabled={loading || uploading}
                    className={`px-5 py-1.5 rounded-full text-white text-sm font-medium flex items-center justify-center transition ${loading || uploading
                      ? "bg-gray-400 cursor-not-allowed"
                      : "bg-blue-600 hover:bg-blue-700"
                      }`}
                  >
                    {loading || uploading ? (
                      <span className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                    ) : (
                      t("post")
                    )}
                  </button>
                )}

              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
